export default {
  title: 'Property Microsite',
  subtitle: 'Configure the public-facing website for this property',
  breadcrumb: 'Microsite',
  tabs: {
    editor: 'Site Settings',
    pages: 'Pages',
  },
  slug: {
    label: 'Site Slug',
    placeholder: 'e.g. peerapat-residence',
    hint: 'Only lowercase letters, numbers, and hyphens.',
    preview_label: 'Public URL',
    error_format: 'Only lowercase letters (a-z), numbers (0-9), and hyphens (-) are allowed.',
    error_required: 'Slug is required.',
  },
  template: {
    label: 'Template',
    description: 'Choose a design template for your property site.',
    modern_name: 'Modern',
    modern_description: 'Clean and minimal. Great for contemporary properties.',
    classic_name: 'Classic',
    classic_description: 'Traditional layout with warm tones. Suits established residences.',
    elegant_name: 'Elegant',
    elegant_description: 'Sophisticated design with premium feel.',
  },
  theme: {
    section_colors: 'Colors',
    section_fonts: 'Fonts',
    section_images: 'Images',
    section_footer: 'Footer',
    primary_color: 'Primary Color',
    secondary_color: 'Secondary Color',
    accent_color: 'Accent Color',
    button_color: 'Button Color',
    button_text_color: 'Button Text Color',
    header_bg_color: 'Header Background',
    header_text_color: 'Header Text',
    footer_bg_color: 'Footer Background',
    footer_text_color: 'Footer Text',
    font_family: 'Body Font',
    heading_font_family: 'Heading Font',
    logo_url: 'Logo',
    logo_hint: 'Recommended: PNG with transparent background, min 200px wide.',
    hero_image_url: 'Hero Image',
    hero_image_hint: 'Recommended: 1920×1080px or wider.',
    favicon_url: 'Favicon',
    favicon_hint: 'Recommended: 32×32px or 64×64px PNG or ICO.',
    footer_text: 'Footer Text',
    footer_text_placeholder: '© 2025 Peerapat Residence. All rights reserved.',
    contact_phone: 'Contact Phone',
    contact_phone_placeholder: '02-xxx-xxxx',
    contact_email: 'Contact Email',
    contact_email_placeholder: 'contact@peerapat.com',
    contact_address: 'Contact Address',
    contact_address_placeholder: '123 Sukhumvit Road, Bangkok 10110',
    section_seo: 'SEO & Social Sharing',
    seo: {
      title: 'SEO & Social Sharing',
      meta_description: 'Meta Description',
      meta_description_placeholder: 'Description for search engine results (150-160 characters)',
      og_title: 'Social Title',
      og_title_placeholder: 'Property name (default)',
      og_description: 'Social Description',
      og_description_placeholder: 'Brief description shown when sharing links',
      og_image: 'Social Image',
      og_image_hint: 'Image shown when sharing on LINE, Facebook, etc.',
    },
    analytics: {
      title: 'Analytics & Tracking',
      gtm_id: 'Google Tag Manager ID',
      gtm_id_placeholder: 'GTM-XXXXXXX',
      ga4_id: 'Google Analytics 4 ID',
      ga4_id_placeholder: 'G-XXXXXXXXXX',
      clarity_id: 'Microsoft Clarity ID',
      clarity_id_placeholder: 'xxxxxxxxxx',
      hint: 'Analytics scripts are gated by cookie consent banner.',
    },
  },
  pages: {
    section_title: 'Pages',
    add_page: 'Add Page',
    edit_page: 'Edit Page',
    delete_page: 'Delete Page',
    move_up: 'Move "{{title}}" up',
    move_down: 'Move "{{title}}" down',
    unpublish_page: 'Unpublish "{{title}}"',
    publish_page: 'Publish "{{title}}"',
    create_defaults: 'Create Default Pages',
    create_defaults_hint: 'Quickly create Home, About, Contact, and Rooms pages.',
    empty_title: 'No pages yet',
    empty_description: 'Add pages to build your property website.',
    page_type_label: 'Page Type',
    page_types: {
      home: 'Home',
      about: 'About',
      contact: 'Contact',
      rooms: 'Rooms',
      room: 'Room',
      custom: 'Custom',
    },
    content_fields: 'Page Content',
    fields: {
      title: 'Title',
      title_placeholder: 'e.g. About Us',
      slug: 'Slug',
      published: 'Published',
      show_in_nav: 'Show in Menu',
    },
    home: {
      section_hero: 'Hero',
      hero_title: 'Hero Title',
      hero_title_placeholder: 'Welcome to Our Residence',
      hero_subtitle: 'Hero Subtitle',
      hero_subtitle_placeholder: 'Modern living in the heart of the city',
      hero_cta: 'Button Text',
      hero_cta_placeholder: 'View Rooms',
      description: 'Description',
      description_placeholder: 'Tell visitors about your property...',
      rooms_title: 'Featured Rooms',
      room_name: 'Room Name',
      room_name_placeholder: 'e.g. Studio',
      room_price: 'Price (THB/mo)',
      room_size: 'Size (sqm)',
      room_image: 'Image',
      add_room: 'Add Room',
    },
    room: {
      room_name: 'Room Name',
      room_name_placeholder: 'e.g. 1 Bedroom',
      price: 'Price (THB/mo)',
      size_sqm: 'Size (sqm)',
      description: 'Description',
      description_placeholder: 'Describe this room type...',
      amenities: 'Amenities',
      amenity_placeholder: 'e.g. Wi-Fi, A/C, Parking',
      add_amenity: 'Add amenity',
      images: 'Room Images',
      images_hint: 'Upload photos of this room type',
      available_rooms: 'Available Rooms',
      add_available_room: 'Add Room',
    },
    contact: {
      heading: 'Heading',
      heading_placeholder: 'Contact Us',
      description: 'Description',
      description_placeholder: 'Let visitors know how to reach you...',
      contact_info: 'Contact Information',
      phone: 'Phone',
      phone_placeholder: '02-xxx-xxxx',
      email: 'Email',
      email_placeholder: 'contact@example.com',
      address: 'Address',
      address_placeholder: '123 Sukhumvit Road, Bangkok 10110',
      google_maps_url: 'Google Maps Embed URL',
      google_maps_url_placeholder: 'https://www.google.com/maps/embed?pb=...',
    },
    button: {
      section: 'Button',
      text: 'Button Text',
      text_placeholder: 'e.g. View Rooms',
      action_type: 'Action Type',
      types: {
        link: 'Link (URL)',
        page: 'Page',
        phone: 'Phone',
        email: 'Email',
        line: 'LINE',
      },
      url: 'URL',
      page_slug: 'Page Slug',
      page_slug_placeholder: 'e.g. room/1',
      phone: 'Phone Number',
      email: 'Email Address',
      line_id: 'LINE ID',
    },
    messages: {
      delete_confirm: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
      create_success: 'Page created.',
      update_success: 'Page updated.',
      delete_success: 'Page deleted.',
      create_error: 'Failed to create page.',
      update_error: 'Failed to update page.',
      delete_error: 'Failed to delete page.',
      creating_defaults: 'Creating default pages...',
    },
  },
  actions: {
    save: 'Save Settings',
    publish: 'Publish',
    publishing: 'Publishing...',
    view_site: 'View Site',
  },
  status: {
    published: 'Published',
    unpublished: 'Unpublished',
    last_published: 'Last published {{date}}',
  },
  messages: {
    save_success: 'Microsite settings saved.',
    save_error: 'Failed to save settings. Please try again.',
    publish_success: 'Site is being published. This may take a moment.',
    publish_error: 'Failed to publish. Please try again.',
  },
  empty: {
    title: 'No microsite configured',
    description: 'Set a slug and template to get started.',
  },
  preview: {
    title: 'Live Preview',
    desktop: 'Desktop',
    mobile: 'Mobile',
    mobile_too_narrow: 'Screen too narrow for mobile preview. Zoom out or widen your browser.',
    nav_home: 'Home',
    nav_rooms: 'Room',
    nav_contact: 'Contact',
    hero_title: 'Welcome to Our Residence',
    hero_subtitle: 'Modern living in the heart of the city',
    hero_cta: 'View Rooms',
    featured_rooms: 'Featured Rooms',
    details: 'Details',
    available_rooms: 'Available Rooms',
    available: 'Available',
    inquire: 'Inquire',
    per_month: '/mo',
    sqm: 'sqm',
    footer_contact: 'Contact',
    footer_phone: 'Phone',
    footer_email: 'Email',
    footer_pages: 'Pages',
    contact_us: 'Contact Us',
    send_message: 'Send a Message',
    form_name: 'Name',
    form_email: 'Email',
    form_message: 'Message',
    send: 'Send',
    book_now: 'Book Now',
    new_badge: 'New',
    template_label: 'template',
    default_address: '123 Sukhumvit Rd, Bangkok',
    room_description: 'Spacious room with modern furnishings, air conditioning, and a private balcony overlooking the city. Includes Wi-Fi, parking, and 24-hour security.',
    studio: 'Studio',
    one_br: '1 Bedroom',
    two_br: '2 Bedroom',
    penthouse: 'Penthouse',
  },
  card: {
    status_published: 'Published',
    status_publishing: 'Publishing...',
    status_failed: 'Publish Failed',
    status_draft: 'Draft',
    last_published: 'Last published {{date}}',
    never_published: 'Never published',
    edit_button: 'Edit Microsite',
    setup_button: 'Set Up Microsite',
  },
}
